import { useEffect, useMemo, useState } from 'react'
import { Link, useNavigate } from '@tanstack/react-router'
import { ArrowLeft, CheckCircle2, Download, Loader2, MicVocal, Upload } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Textarea } from '@/components/ui/textarea'
import { uploadFile } from '@/shared/api/app-client'
import type { ApiResponse, AssetUploadResult } from '@/shared/api/app-types'
import {
  ALIYUN_TTS_LANGUAGE_OPTIONS,
  ALIYUN_TTS_VOICE_PROFILES,
  VOICE_AGE_OPTIONS,
  VOICE_GENDER_OPTIONS,
  VOICE_USE_CASE_OPTIONS,
  aliyunTtsService,
  getAliyunVoiceProfile,
  type AliyunTtsLanguage,
  type AliyunTtsVoice,
  type VoiceAge,
  type VoiceGender,
  type VoiceUseCase,
} from '@/features/voiceover/services/aliyun-tts-service'

type FilterValue = 'all'
type VoiceFilters = {
  gender: FilterValue | VoiceGender
  age: FilterValue | VoiceAge
  useCase: FilterValue | VoiceUseCase
}

const defaultFilters: VoiceFilters = {
  gender: 'all',
  age: 'all',
  useCase: 'all',
}

const allOption = { value: 'all', label: '全部' } as const

function formatDuration(seconds: number) {
  const total = Math.max(0, Math.round(seconds))
  const minutes = Math.floor(total / 60)
  const remain = total % 60
  return `${minutes}:${String(remain).padStart(2, '0')}`
}

function findOptionLabel<T extends string>(
  value: T,
  options: ReadonlyArray<{ value: T; label: string }>,
) {
  return options.find((option) => option.value === value)?.label ?? value
}

function VoiceFilterSelect<T extends string>({
  label,
  value,
  options,
  onValueChange,
}: {
  label: string
  value: T | FilterValue
  options: ReadonlyArray<{ value: T; label: string }>
  onValueChange: (value: T | FilterValue) => void
}) {
  return (
    <div className="space-y-1.5">
      <Label className="text-xs text-muted-foreground">{label}</Label>
      <Select value={value} onValueChange={(next) => onValueChange(next as T | FilterValue)}>
        <SelectTrigger className="h-8">
          <SelectValue placeholder={`选择${label}`} />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="all">{allOption.label}</SelectItem>
          {options.map((option) => (
            <SelectItem key={option.value} value={option.value}>
              {option.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  )
}

export function AiVoiceoverCanvas() {
  const navigate = useNavigate()
  const [text, setText] = useState(
    '今天给大家分享一个更高效的视频生产方式：一个长视频素材，可以快速变成多个平台版本，再进入编辑器统一精修和发布。',
  )
  const [title, setTitle] = useState('JIAN AI 配音示例')
  const [voice, setVoice] = useState<AliyunTtsVoice>('Cherry')
  const [languageType, setLanguageType] = useState<AliyunTtsLanguage>('Chinese')
  const [instructions, setInstructions] = useState('')
  const [optimizeInstructions, setOptimizeInstructions] = useState(false)
  const [filters, setFilters] = useState<VoiceFilters>(defaultFilters)
  const [progress, setProgress] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [isGenerating, setIsGenerating] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [savedAsset, setSavedAsset] = useState<AssetUploadResult | null>(null)
  const [result, setResult] = useState<{
    file: File
    objectUrl: string
    duration: number
  } | null>(null)

  const filteredVoices = useMemo(
    () =>
      ALIYUN_TTS_VOICE_PROFILES.filter((profile) => {
        if (filters.gender !== 'all' && profile.gender !== filters.gender) return false
        if (filters.age !== 'all' && profile.age !== filters.age) return false
        if (filters.useCase !== 'all' && !profile.useCases.includes(filters.useCase)) return false
        return true
      }),
    [filters],
  )

  useEffect(() => {
    if (filteredVoices.some((item) => item.value === voice)) {
      return
    }

    if (filteredVoices[0]) {
      setVoice(filteredVoices[0].value as AliyunTtsVoice)
    }
  }, [filteredVoices, voice])

  const selectedVoice = useMemo(() => getAliyunVoiceProfile(voice), [voice])
  const canGenerate = text.trim().length > 0 && !isGenerating && !!selectedVoice
  const canSave = !!result && !isSaving

  const handleGenerate = async () => {
    if (!canGenerate || !selectedVoice) return

    setIsGenerating(true)
    setError(null)
    setProgress(null)
    setSavedAsset(null)

    try {
      if (result) {
        URL.revokeObjectURL(result.objectUrl)
      }

      const generated = await aliyunTtsService.generateSpeechFile({
        text,
        voice: selectedVoice.value,
        languageType,
        instructions,
        optimizeInstructions,
        onProgress: setProgress,
      })

      const objectUrl = URL.createObjectURL(generated.blob)
      setResult({
        file: generated.file,
        objectUrl,
        duration: generated.duration,
      })
      setProgress('生成完成，可以试听、下载，或保存到素材库。')
    } catch (generationError) {
      setError(generationError instanceof Error ? generationError.message : 'AI 配音生成失败。')
      setProgress(null)
    } finally {
      setIsGenerating(false)
    }
  }

  const handleDownload = () => {
    if (!result) return

    const anchor = document.createElement('a')
    anchor.href = result.objectUrl
    anchor.download = result.file.name
    anchor.click()
  }

  const handleSaveAsset = async () => {
    if (!result || isSaving) return

    setIsSaving(true)
    setError(null)

    try {
      const normalizedTitle = title.trim() || 'ai-voiceover'
      const fileToUpload = new File([result.file], `${normalizedTitle}.wav`, {
        type: result.file.type || 'audio/wav',
        lastModified: result.file.lastModified,
      })
      const uploadRes = await uploadFile<ApiResponse<AssetUploadResult>>(
        '/api/assets/upload',
        fileToUpload,
      )
      setSavedAsset(uploadRes.data)
      setProgress('已保存到素材库，可以继续进入编辑器或在素材库里复用。')
    } catch (saveError) {
      setError(saveError instanceof Error ? saveError.message : '保存到素材库失败。')
    } finally {
      setIsSaving(false)
    }
  }

  const handleFilterChange = <K extends keyof VoiceFilters>(key: K, value: VoiceFilters[K]) => {
    setFilters((current) => ({ ...current, [key]: value }))
  }

  const activeFilterCount = Object.values(filters).filter((value) => value !== 'all').length

  return (
    <div className="mx-auto max-w-7xl px-6 py-8">
      {/* Header */}
      <div className="mb-8 flex items-center gap-4">
        <Link
          to="/app"
          className="inline-flex size-9 items-center justify-center rounded-lg border border-border bg-card text-foreground transition-colors hover:bg-accent"
        >
          <ArrowLeft className="size-4" />
        </Link>
        <div>
          <h1 className="text-2xl font-semibold tracking-tight text-foreground">AI 配音</h1>
          <p className="mt-0.5 text-sm text-muted-foreground">
            输入文案，选择音色，生成可下载或沉淀到素材库的旁白音频。
          </p>
        </div>
      </div>

      <div className="grid gap-8 xl:grid-cols-[1.15fr_0.85fr]">
        {/* Left: generation settings */}
        <section className="space-y-6">
          {/* Script */}
          <div className="space-y-3">
            <Label htmlFor="voiceover-title" className="text-sm font-medium">
              素材标题
            </Label>
            <Input
              id="voiceover-title"
              value={title}
              onChange={(event) => setTitle(event.target.value)}
              placeholder="给这段配音起个名字"
            />
          </div>

          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <Label htmlFor="voiceover-text" className="text-sm font-medium">
                配音文案
              </Label>
              <span className="text-xs text-muted-foreground">{text.trim().length} 字</span>
            </div>
            <Textarea
              id="voiceover-text"
              value={text}
              onChange={(event) => setText(event.target.value)}
              placeholder="输入要生成的旁白文案"
              className="min-h-[180px] resize-y"
            />
          </div>

          {/* Language + style instructions */}
          <div className="grid gap-5 sm:grid-cols-2">
            <div className="space-y-3">
              <Label className="text-sm font-medium">语种</Label>
              <Select
                value={languageType}
                onValueChange={(value) => setLanguageType(value as AliyunTtsLanguage)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="选择语种" />
                </SelectTrigger>
                <SelectContent>
                  {ALIYUN_TTS_LANGUAGE_OPTIONS.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <Label htmlFor="voiceover-instructions" className="text-sm font-medium">
                  说话风格
                </Label>
                <label className="flex cursor-pointer items-center gap-1.5 text-xs text-muted-foreground">
                  <input
                    type="checkbox"
                    checked={optimizeInstructions}
                    onChange={(event) => setOptimizeInstructions(event.target.checked)}
                    className="size-3.5 accent-primary"
                  />
                  优化指令
                </label>
              </div>
              <Input
                id="voiceover-instructions"
                value={instructions}
                onChange={(event) => setInstructions(event.target.value)}
                placeholder="如：语速稍快、亲切自然，适合产品介绍"
              />
            </div>
          </div>

          {/* Voice selection */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <Label className="text-sm font-medium">音色</Label>
              {activeFilterCount > 0 ? (
                <button
                  type="button"
                  onClick={() => setFilters(defaultFilters)}
                  className="text-xs text-muted-foreground transition-colors hover:text-foreground"
                >
                  清除筛选
                </button>
              ) : null}
            </div>

            {/* Compact filters */}
            <div className="grid grid-cols-3 gap-3">
              <VoiceFilterSelect
                label="性别"
                value={filters.gender}
                options={VOICE_GENDER_OPTIONS}
                onValueChange={(value) => handleFilterChange('gender', value)}
              />
              <VoiceFilterSelect
                label="年龄"
                value={filters.age}
                options={VOICE_AGE_OPTIONS}
                onValueChange={(value) => handleFilterChange('age', value)}
              />
              <VoiceFilterSelect
                label="用途"
                value={filters.useCase}
                options={VOICE_USE_CASE_OPTIONS}
                onValueChange={(value) => handleFilterChange('useCase', value)}
              />
            </div>

            {/* Compact voice list */}
            <div className="max-h-[320px] space-y-1 overflow-y-auto">
              {filteredVoices.length > 0 ? (
                filteredVoices.map((profile) => {
                  const selected = profile.value === voice
                  return (
                    <button
                      key={profile.value}
                      type="button"
                      onClick={() => setVoice(profile.value as AliyunTtsVoice)}
                      className={`flex w-full items-center justify-between gap-3 rounded-lg border px-3.5 py-2.5 text-left transition-colors ${
                        selected
                          ? 'border-primary/40 bg-primary/5'
                          : 'border-border bg-card hover:bg-accent'
                      }`}
                    >
                      <div className="min-w-0 flex-1">
                        <div className="flex items-baseline gap-2">
                          <span className="text-sm font-medium text-foreground">
                            {profile.label}
                          </span>
                          <span className="text-xs text-muted-foreground">{profile.value}</span>
                        </div>
                        <p className="mt-0.5 truncate text-xs text-muted-foreground">
                          {profile.description}
                        </p>
                      </div>
                      <span className="shrink-0 rounded border border-border px-1.5 py-0.5 text-[11px] text-muted-foreground">
                        {findOptionLabel(profile.gender, VOICE_GENDER_OPTIONS)}
                      </span>
                    </button>
                  )
                })
              ) : (
                <div className="rounded-lg border border-dashed border-border px-4 py-8 text-center text-sm text-muted-foreground">
                  当前筛选条件下没有匹配音色，试试放宽一两个维度。
                </div>
              )}
            </div>
          </div>

          {/* Generate (primary action stays with the script) */}
          <Button onClick={() => void handleGenerate()} disabled={!canGenerate} className="w-full sm:w-auto">
            {isGenerating ? <Loader2 className="size-4 animate-spin" /> : <MicVocal className="size-4" />}
            生成配音
          </Button>

          {progress ? (
            <div className="rounded-lg border border-primary/15 bg-primary/5 px-3.5 py-2.5 text-sm text-primary">
              {progress}
            </div>
          ) : null}

          {error ? (
            <div className="rounded-lg border border-destructive/20 bg-destructive/5 px-3.5 py-2.5 text-sm text-destructive">
              {error}
            </div>
          ) : null}
        </section>

        {/* Right: preview */}
        <section className="space-y-5">
          {result ? (
            <>
              <div className="space-y-3 rounded-xl border border-border bg-card p-5">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <div className="text-sm font-medium text-foreground">
                      {title.trim() || '未命名配音'}
                    </div>
                    <div className="mt-1 text-xs text-muted-foreground">
                      {selectedVoice?.label ?? voice} · {languageType} ·{' '}
                      {formatDuration(result.duration)}
                    </div>
                  </div>
                  <div className="rounded-lg bg-primary/10 p-2.5 text-primary">
                    <MicVocal className="size-5" />
                  </div>
                </div>

                <audio controls src={result.objectUrl} className="w-full" />
              </div>

              {/* Result actions */}
              <div className="flex gap-3">
                <Button
                  variant="outline"
                  className="flex-1"
                  onClick={handleDownload}
                  disabled={!result}
                >
                  <Download className="size-4" />
                  下载音频
                </Button>
                <Button
                  variant="outline"
                  className="flex-1"
                  onClick={() => void handleSaveAsset()}
                  disabled={!canSave}
                >
                  {isSaving ? <Loader2 className="size-4 animate-spin" /> : <Upload className="size-4" />}
                  保存到素材库
                </Button>
              </div>

              <div className="space-y-3">
                <Button
                  variant="outline"
                  className="w-full justify-start"
                  onClick={() => navigate({ to: '/app/assets', search: { tab: 'audio' } })}
                >
                  去素材库查看
                </Button>
                <Button
                  variant="outline"
                  className="w-full justify-start"
                  onClick={() => navigate({ to: '/projects/new' })}
                >
                  新建项目并进入编辑器
                </Button>
              </div>

              {savedAsset ? (
                <div className="rounded-lg border border-emerald-500/20 bg-emerald-500/5 px-3.5 py-2.5 text-sm text-emerald-700">
                  <div className="flex items-center gap-2 font-medium">
                    <CheckCircle2 className="size-4" />
                    已保存到素材库
                  </div>
                  <div className="mt-1 text-xs text-emerald-700/80">
                    素材 ID: {savedAsset.id} · 文件名: {savedAsset.fileName}
                  </div>
                </div>
              ) : null}
            </>
          ) : (
            <div className="flex min-h-[280px] flex-col items-center justify-center rounded-xl border border-dashed border-border px-8 text-center">
              <MicVocal className="size-8 text-muted-foreground/50" />
              <h3 className="mt-4 text-sm font-medium text-foreground">生成结果会在这里展示</h3>
              <p className="mt-1.5 max-w-xs text-xs leading-5 text-muted-foreground">
                在左侧填写文案并生成后，可在这里试听、下载或保存到素材库。
              </p>
            </div>
          )}
        </section>
      </div>
    </div>
  )
}
